import { useFormik } from "formik";
import { useAtom } from "jotai";
import { FC, useEffect, useState } from "react";
import { toast } from "sonner";
import * as yup from "yup";
import { formsSubmitAtom } from "../../../atom/atom";
import CustomSelect from "../../../components/forms/CustomSelect";
import RequiredInput from "../../../components/forms/InputHOC";
import useHandleErrors from "../../../hooks/useHandleErrors";
import { RouteService } from "../../../services/route/routeService";
import { Route } from "../../../type/entities/route-type";
import cn from "../../../utils/classNames";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import useCompanies from "../../../hooks/api-calls/useCompanies";
import { Input } from "rizzui";

interface RouteFormProps {
    setOpenModal: any;
    refetch: () => void;
    route?: Route | null;
    formRef?: any;
}

const schema = yup.object({
    name: yup.string()
        .min(1, 'Demasiado corto')
        .max(100, 'Demasiado largo')
        .required('Campo obligatorio'),
    description: yup.string()
        .max(500, 'Demasiado largo')
        .nullable(),
    status: yup.boolean()
        .required('Campo obligatorio'),
    radioInclusion: yup.number()
        .min(0, 'El radio de inclusión debe ser un número positivo o cero')
        .required('Campo obligatorio'),
});

const superAdminSchema = schema.concat(yup.object({
    company: yup.string().required('La compañía es obligatoria'),
}))

const RouteForm: FC<RouteFormProps> = ({ setOpenModal, refetch, route, formRef }) => {

    // STATES

    const service = new RouteService();
    const isEdit = route !== null;
    const [isSearched, setIsSearched] = useState<boolean>(false);

    // HOOKS

    const [, setIsSubmitting] = useAtom(formsSubmitAtom);
    const { handleErrors } = useHandleErrors();
    const { userCan } = usePrivilege();
    const { getCompaniesList } = useCompanies();

    // FORMIK

    const formik = useFormik({
        initialValues: {
            id: route?.id ?? '',
            name: route?.name ?? '',
            description: route?.description ?? '',
            status: route?.status ?? true,
            radioInclusion: route?.radioInclusion ?? 0,
            company: route?.company?.id ?? '',
        },
        validationSchema: userCan('list', 'company') ? superAdminSchema : schema,
        validateOnBlur: false,
        onSubmit: values => {
            if (!isEdit) {
                handleCreate(values)
            } else {
                handleEdit(values)
            }
        },
    });

    // FUNCTIONS

    //-----------------------------------------------------------------------------------------------------------------
    /**
     * @ES CREAR UNA NUEVA RUTA
     * @EN CREATE A NEW ROUTE
     * 
     * @param values
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleCreate = async (values: any) => {
        try {
            const response = await (await service.createRoute(values)).getResponseData();
            if (response.success) {
                refetch();
                setOpenModal(false);
                formik.resetForm();
                toast.success('Ruta creada correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-----------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES EDITAR UN MANTENIMIENTO
     * @EN EDIT A MAINTENANCE
     * 
     * @param values
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleEdit = async (values: any) => {
        try {
            const response = await (await service.editRoute(values)).getResponseData();
            if (response.success) {
                refetch();
                setOpenModal(false);
                formik.resetForm();
                toast.success('Ruta editada correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    // USE EFFECT

    useEffect(() => {
        setIsSubmitting(formik.isSubmitting);
    }, [formik.isSubmitting, setIsSubmitting]);

    useEffect(() => {
        if (!formik.values.company && getCompaniesList().length > 0 && !isSearched) {
            setIsSearched(true);
            let defaultCompany = getCompaniesList()?.find((g: any) => g.label == 'GESTION DE RESIDUOS 2023 SLU');
            formik.setFieldValue('company', defaultCompany?.value ?? '');
        }
    }, [getCompaniesList]);

    // RENDER

    return (
        <div className="@container">
            <form onSubmit={formik.handleSubmit} ref={formRef ? formRef : undefined}>
                <div className={'md:col-span-12'}>
                    <div className='md:grid md:grid-cols-12 gap-4'>

                        <RequiredInput
                            id='name'
                            label="Nombre"
                            placeholder="Ingrese un nombre"
                            type="text"
                            className='col-span-12'
                            formik={formik}
                            error={formik.errors.name}
                            required={true}
                        />

                        <Input
                            id='description'
                            label="Descripción"
                            placeholder="Ingrese una descripción"
                            type="text"
                            className='col-span-12'
                            value={formik.values.description}
                            onChange={(e: any) => formik.setFieldValue('description', e.target.value)}
                            error={formik.errors.description}
                            required={true}
                        />

                        <CustomSelect
                            id='status'
                            label="Estado"
                            options={[
                                { value: true, label: 'Activa' },
                                { value: false, label: 'Inactiva' },
                            ]}
                            value={{ value: formik.values.status, label: formik.values.status ? 'Activa' : 'Inactiva' }}
                            containerClassName={cn('col-span-12', isEdit ? "pointer-events-none" : "")}
                            onChange={(value: any) => formik.setFieldValue('status', value.value)}
                            error={formik.errors.status}
                            required={true}
                        />

                        <RequiredInput
                            id='radioInclusion'
                            label="Radio de inclusión"
                            type="number"
                            step="0.01"
                            min="0"
                            className='col-span-12'
                            formik={formik}
                            error={formik.errors.radioInclusion}
                            required={true}
                        />

                        {userCan('list', 'company') && (
                            <CustomSelect
                                isSearchable
                                required
                                containerClassName="col-span-12"
                                id="companyId"
                                label="Compañía"
                                placeholder="Selecciona una compañía"
                                options={getCompaniesList()}
                                value={
                                    formik.values.company ?
                                    { value: formik.values.company, label: getCompaniesList()?.find((g: any) => g.value === formik.values.company)?.label } :
                                    getCompaniesList()?.find((g: any) => g.label == 'GESTION DE RESIDUOS 2023 SLU')
                                }
                                onChange={(selectedOption: any) => {
                                    formik.setFieldValue('company', selectedOption?.value || '');
                                }}
                                error={formik.touched.company && formik.errors.company ? (typeof formik.errors.company === 'string' ? formik.errors.company : '') : ''}
                            />
                        )}

                    </div>
                </div>
            </form>
        </div>
    )
}

export default RouteForm;